import React, { useEffect, useState } from 'react';
import { Button, Row, Col, Typography, Tag, Divider, Input, message } from 'antd';
import { useHistory, useParams } from 'react-router-dom';
import { trackBlogView, trackBlogEngagement, trackNewsletterSignup } from '../../utils/analytics';
import { ArrowLeftOutlined, CalendarOutlined, UserOutlined, ClockCircleOutlined } from '@ant-design/icons';
import { getPostBySlug, blogPosts } from '../../data/blogPosts';
import ReactMarkdown from 'react-markdown';
import rehypeRaw from 'rehype-raw';
import rehypeSanitize from 'rehype-sanitize';
import SEOHead from '../../components/SEOHead';
import InternalLinks from '../../components/InternalLinks';
import { generateArticleSchema, generateBreadcrumbSchema } from '../../utils/schemaMarkup';
import '../../styles/mobile.css';
import '../../styles/blog-visuals.css';

const { Title, Text, Paragraph } = Typography;

const BlogPost = () => {
  const history = useHistory();
  const { slug } = useParams();
  const post = getPostBySlug(slug);
  const [startTime] = useState(Date.now());
  const [email, setEmail] = useState('');
  const [isSubscribing, setIsSubscribing] = useState(false);

  // Track blog view on component mount
  useEffect(() => {
    if (post) {
      trackBlogView(post);
    }
  }, [post]);

  // Track engagement time on component unmount
  useEffect(() => {
    return () => {
      if (post) {
        const engagementTime = (Date.now() - startTime) / 1000; // in seconds
        if (engagementTime > 10) { // Only track if user stayed more than 10 seconds
          trackBlogEngagement(post, engagementTime);
        }
      }
    };
  }, [post, startTime]);

  if (!post) {
    return (
      <div style={{ minHeight: '100vh', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ textAlign: 'center' }}>
          <div style={{ fontSize: '64px', marginBottom: '20px', opacity: 0.3 }}>📝</div>
          <Title level={3} style={{ color: '#7f8c8d' }}>Article not found</Title>
          <Text type="secondary">The article you're looking for doesn't exist.</Text>
          <br />
          <Button 
            type="primary" 
            style={{ marginTop: '16px', background: '#667eea', borderColor: '#667eea' }}
            onClick={() => history.push('/blog')}
          >
            Back to Blog
          </Button>
        </div>
      </div>
    );
  }

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString(undefined, options);
  };

  const getRelatedPosts = () => {
    return blogPosts
      .filter(p => p.id !== post.id && (p.category === post.category || p.tags.some(tag => post.tags.includes(tag))))
      .slice(0, 3);
  };

  const handleNewsletterSignup = async () => {
    if (!email || !email.includes('@')) {
      message.error('Please enter a valid email address');
      return;
    }

    setIsSubscribing(true);
    
    try {
      trackNewsletterSignup('blog_post');
      await new Promise(resolve => setTimeout(resolve, 1000));
      message.success('Thank you! You\'ll receive our latest insights on children\'s emotional wellness.');
      setEmail('');
    } catch (error) {
      message.error('Something went wrong. Please try again.');
    } finally {
      setIsSubscribing(false);
    }
  };

  // Generate structured data for blog post
  const breadcrumbs = [
    { name: 'Home', url: '/' },
    { name: 'Blog', url: '/blog' },
    { name: post.title, url: `/blog/${post.slug}` }
  ];

  const articleSchema = {
    "@context": "https://schema.org",
    "@graph": [
      generateArticleSchema(post),
      generateBreadcrumbSchema(breadcrumbs)
    ]
  };

  return (
    <div style={{ minHeight: '100vh', background: '#fff' }}>
      <SEOHead
        title={post.seoTitle || post.title}
        description={post.seoDescription || post.excerpt}
        keywords={post.tags?.join(', ')}
        canonicalUrl={`/blog/${post.slug}`}
        ogType="article"
        ogImage={post.image}
        structuredData={articleSchema}
      />
      
      
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: '20px 0',
        marginBottom: '0'
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '0 20px' }}>
          <Button 
            type="text" 
            icon={<ArrowLeftOutlined />} 
            onClick={() => history.push('/blog')}
            style={{ color: 'white' }}
          >
            Back to Blog
          </Button>
        </div>
      </div>

      <div style={{ paddingTop: '40px', paddingBottom: '60px' }}>
        <div style={{ maxWidth: '800px', margin: '0 auto', padding: '0 20px' }}>
          {/* Article Header */}
          <div style={{ textAlign: 'center', marginBottom: '40px' }}>
            <div style={{ marginBottom: '16px' }}>
              <Tag color="blue" style={{ fontSize: '14px', padding: '4px 12px' }}>
                {post.category}
              </Tag>
            </div>
            <Title level={1} style={{ 
              fontSize: '2.5rem', 
              fontWeight: '700',
              color: '#2c3e50',
              marginBottom: '20px',
              lineHeight: '1.2'
            }}>
              {post.title}
            </Title>
            <Paragraph style={{ 
              fontSize: '18px', 
              color: '#7f8c8d', 
              marginBottom: '24px',
              fontStyle: 'italic'
            }}>
              {post.excerpt}
            </Paragraph>
            
            {/* Meta Information */}
            <div style={{ 
              display: 'flex', 
              justifyContent: 'center', 
              gap: '24px', 
              flexWrap: 'wrap',
              color: '#7f8c8d',
              fontSize: '14px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <UserOutlined />
                <Text type="secondary">By {post.author}</Text>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <CalendarOutlined />
                <Text type="secondary">{formatDate(post.publishedDate)}</Text>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <ClockCircleOutlined />
                <Text type="secondary">{post.readTime}</Text>
              </div>
            </div>
          </div>

          {/* Article Cover */}
          <div style={{ 
            height: '300px', 
            borderRadius: '15px',
            marginBottom: '40px',
            overflow: 'hidden'
          }}>
            <img 
              src={post.image} 
              alt={post.title}
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'cover',
                borderRadius: '15px'
              }}
              onError={(e) => {
                e.target.style.display = 'none';
                e.target.parentNode.innerHTML = '<div style="height: 300px; background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%); border-radius: 15px; display: flex; align-items: center; justify-content: center; font-size: 80px;">📖</div>';
              }}
            />
          </div>

          {/* Article Content */}
          <div style={{ 
            fontSize: '16px', 
            lineHeight: '1.8', 
            color: '#2c3e50',
            marginBottom: '40px'
          }}>
            <ReactMarkdown
              rehypePlugins={[rehypeRaw, rehypeSanitize]}
              components={{
                h1: ({children}) => (
                  <Title level={2} style={{
                    marginTop: '40px', 
                    marginBottom: '20px', 
                    color: '#2c3e50',
                    fontSize: '28px',
                    fontWeight: '700',
                    borderBottom: '2px solid #667eea',
                    paddingBottom: '8px'
                  }}>
                    {children}
                  </Title>
                ),
                h2: ({children}) => (
                  <Title level={3} style={{
                    marginTop: '32px', 
                    marginBottom: '16px', 
                    color: '#2c3e50',
                    fontSize: '22px',
                    fontWeight: '600'
                  }}>
                    {children}
                  </Title>
                ),
                h3: ({children}) => (
                  <Title level={4} style={{
                    marginTop: '28px', 
                    marginBottom: '12px', 
                    color: '#667eea',
                    fontSize: '18px',
                    fontWeight: '600'
                  }}>
                    {children}
                  </Title>
                ),
                p: ({children}) => (
                  <Paragraph style={{
                    marginBottom: '18px', 
                    fontSize: '16px', 
                    lineHeight: '1.8',
                    color: '#4a5568'
                  }}>
                    {children}
                  </Paragraph>
                ),
                ul: ({children}) => (
                  <ul style={{
                    marginBottom: '20px', 
                    paddingLeft: '28px',
                    lineHeight: '1.7'
                  }}>
                    {children}
                  </ul>
                ),
                ol: ({children}) => (
                  <ol style={{
                    marginBottom: '20px', 
                    paddingLeft: '28px',
                    lineHeight: '1.7'
                  }}>
                    {children}
                  </ol>
                ),
                li: ({children}) => (
                  <li style={{
                    marginBottom: '10px', 
                    lineHeight: '1.7',
                    color: '#4a5568'
                  }}>
                    {children}
                  </li>
                ),
                strong: ({children}) => (
                  <strong style={{
                    color: '#2d3748', 
                    fontWeight: '700'
                  }}>
                    {children}
                  </strong>
                ),
                em: ({children}) => (
                  <em style={{
                    color: '#667eea',
                    fontStyle: 'italic'
                  }}>
                    {children}
                  </em>
                ),
                blockquote: ({children}) => (
                  <div style={{
                    borderLeft: '4px solid #667eea',
                    margin: '24px 0',
                    background: 'linear-gradient(135deg, #f7fafc 0%, #edf2f7 100%)',
                    padding: '20px 24px',
                    borderRadius: '8px',
                    fontStyle: 'italic',
                    fontSize: '15px',
                    color: '#4a5568'
                  }}>
                    {children}
                  </div>
                ),
                a: ({children, href}) => (
                  <a 
                    href={href}
                    style={{
                      color: '#667eea',
                      textDecoration: 'underline',
                      fontWeight: '500'
                    }}
                    onMouseEnter={(e) => e.target.style.color = '#4c51bf'}
                    onMouseLeave={(e) => e.target.style.color = '#667eea'}
                  >
                    {children}
                  </a>
                ),
                code: ({children}) => (
                  <code style={{
                    background: '#f7fafc',
                    padding: '2px 6px',
                    borderRadius: '4px',
                    fontSize: '14px',
                    fontFamily: 'Monaco, Consolas, monospace',
                    border: '1px solid #e2e8f0'
                  }}>
                    {children}
                  </code>
                ),
                table: ({children}) => (
                  <table className="comparison-table">
                    {children}
                  </table>
                ),
                thead: ({children}) => <thead>{children}</thead>,
                tbody: ({children}) => <tbody>{children}</tbody>,
                tr: ({children}) => <tr>{children}</tr>,
                th: ({children}) => <th>{children}</th>,
                td: ({children}) => <td>{children}</td>,
                div: ({children, className}) => (
                  <div className={className}>
                    {children}
                  </div>
                )
              }}
            >
              {post.content}
            </ReactMarkdown>
          </div>

          {/* Internal Links for SEO */}
          <InternalLinks 
            currentSlug={post.slug}
            currentTags={post.tags}
            currentCategory={post.category}
            title="Related Resources & Guides"
          />

          <Divider />

          {/* Tags */}
          <div style={{ marginBottom: '40px' }}>
            <Text strong style={{ marginRight: '12px', color: '#7f8c8d' }}>Tags:</Text>
            {post.tags.map(tag => (
              <Tag key={tag} style={{ marginBottom: '8px' }}>
                {tag}
              </Tag>
            ))}
          </div>

          <Divider />

          {/* Author Info */}
          <div style={{ 
            background: '#f8f9fa', 
            padding: '24px', 
            borderRadius: '12px',
            marginBottom: '40px'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <div style={{
                width: '60px',
                height: '60px',
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                color: 'white',
                fontSize: '24px',
                fontWeight: 'bold'
              }}>
                {post.author.split(' ').map(name => name[0]).join('')}
              </div>
              <div>
                <Title level={5} style={{ margin: 0, marginBottom: '4px' }}>
                  {post.author}
                </Title>
                <Text type="secondary">
                  Contributing expert in child development and emotional wellness
                </Text>
              </div>
            </div>
          </div>

          {/* Related Posts */}
          {getRelatedPosts().length > 0 && (
            <div>
              <Title level={3} style={{ marginBottom: '24px', color: '#2c3e50' }}>
                Related Articles
              </Title>
              <Row gutter={[16, 16]}>
                {getRelatedPosts().map(relatedPost => (
                  <Col xs={24} md={8} key={relatedPost.id}>
                    <div 
                      style={{ 
                        border: '1px solid #e8e8e8',
                        borderRadius: '8px',
                        padding: '16px',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        height: '100%'
                      }}
                      onClick={() => history.push(`/blog/${relatedPost.slug}`)}
                      onMouseEnter={(e) => {
                        e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.1)';
                        e.currentTarget.style.borderColor = '#667eea';
                      }}
                      onMouseLeave={(e) => {
                        e.currentTarget.style.boxShadow = 'none';
                        e.currentTarget.style.borderColor = '#e8e8e8';
                      }}
                    >
                      <Tag color="blue" style={{ marginBottom: '8px' }}>
                        {relatedPost.category}
                      </Tag>
                      <Title level={5} style={{ marginBottom: '8px' }}>
                        {relatedPost.title}
                      </Title>
                      <Text type="secondary" style={{ fontSize: '14px' }}>
                        {relatedPost.readTime}
                      </Text>
                    </div>
                  </Col>
                ))}
              </Row>
            </div>
          )}

          {/* Newsletter Signup */}
          <div style={{ 
            marginTop: '60px',
            background: '#f8f9fa',
            borderRadius: '12px',
            padding: '40px',
            textAlign: 'center'
          }}>
            <Title level={4} style={{ marginBottom: '12px', color: '#2c3e50' }}>
              Get More Insights Like This
            </Title>
            <Paragraph style={{ color: '#7f8c8d', marginBottom: '24px', maxWidth: '400px', margin: '0 auto 24px auto' }}>
              Join other parents getting practical, research-backed guidance on children's emotional wellness.
            </Paragraph>
            <Row justify="center">
              <Col xs={24} sm={20} md={16}>
                <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', justifyContent: 'center' }}>
                  <Input
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onPressEnter={handleNewsletterSignup}
                    style={{
                      borderRadius: '8px',
                      border: '1px solid #d9d9d9',
                      flex: 1,
                      minWidth: '250px'
                    }}
                  />
                  <Button
                    type="primary"
                    loading={isSubscribing}
                    onClick={handleNewsletterSignup}
                    style={{
                      background: '#667eea',
                      borderColor: '#667eea',
                      borderRadius: '8px',
                      fontWeight: '600'
                    }}
                  >
                    Subscribe
                  </Button>
                </div>
                <Text style={{ fontSize: '12px', color: '#999', marginTop: '8px', display: 'block' }}>
                  No spam. Unsubscribe anytime.
                </Text>
              </Col>
            </Row>
          </div>

          {/* Call to Action */}
          <div style={{ 
            marginTop: '60px',
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            borderRadius: '15px',
            padding: '32px',
            textAlign: 'center',
            color: 'white'
          }}>
            <Title level={4} style={{ color: 'white', marginBottom: '12px' }}>
              Ready to Support Your Child's Emotional Journey?
            </Title>
            <Paragraph style={{ color: 'rgba(255,255,255,0.9)', marginBottom: '20px' }}>
              Discover Lumi Bears - wellness companions designed to provide comfort and emotional support for children.
            </Paragraph>
            <Button 
              type="primary" 
              size="large"
              style={{
                background: 'rgba(255,255,255,0.2)',
                borderColor: 'rgba(255,255,255,0.3)',
                color: 'white',
                fontWeight: 'bold',
                marginRight: '12px'
              }}
              onClick={() => history.push('/')}
            >
              Explore Products
            </Button>
            <Button 
              size="large"
              style={{
                background: 'transparent',
                borderColor: 'rgba(255,255,255,0.5)',
                color: 'white'
              }}
              onClick={() => history.push('/blog')}
            >
              More Articles
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogPost;